import { type ReactNode, useEffect, useId, useRef } from "react";

import {
  BottomSheet,
  type BottomSheetDialogProps,
  type BottomSheetTitleType,
} from "../BottomSheet";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useFocusTrap, type UseFocusTrapOptions } from "../hooks/useFocusTrap";
import { ArrowLeftIcon, CrossIcon } from "../Icons";
import { Typo } from "../Typo";

export type ResponsiveModalProps = {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when the modal requests to close (backdrop click, X button) */
  onClose: () => void;
  /** Whether to render as a bottom sheet (mobile) or centered modal (desktop) */
  isMobile: boolean;
  /** Optional supporting text rendered under the title */
  subtitle?: ReactNode;
  /**
   * Render the header close (X) button (default: true). Pass `false` together
   * with `isDismissable={false}` for a modal that can only be left through
   * its own footer action.
   */
  showCloseButton?: boolean;
  /** Modal body content */
  children: ReactNode;
  /** Optional footer content (buttons, etc.) */
  footer?: ReactNode;
  /** Optional back button handler — renders a "< Back" button in the header */
  onBack?: () => void;
  /** Label for the back button (defaults to "Back") */
  backLabel?: string;
  /** Max width of the desktop modal (default: 512px) */
  maxWidth?: number;
  /** Optional prop to enable dragging the mobile bottom sheet to dismiss */
  mobileSheetDraggable?: boolean;
  /** Title typography of the mobile bottom sheet (default: "labelMD") */
  mobileSheetTitleType?: BottomSheetTitleType;
  maxHeight?: number;
  modalClassName?: string;
  headerClassName?: string;
  contentContainerClassName?: string;
  footerContainerClassName?: string;
  /** Override the content area overflow (default: "auto"). Use "visible" to allow dropdowns to escape the scroll container. */
  contentOverflow?: "auto" | "visible";
  isDismissable?: boolean;
  /**
   * Opt out of this modal's own Escape handling. Set this when the caller
   * already owns Escape itself — e.g. to run its own logic first for a
   * layer stacked above this modal (`CreateProgramWizard`'s discard
   * confirmation) — so this modal doesn't close from underneath it.
   */
  disableEscapeHandling?: boolean;
  /**
   * Ref to the scrolling content area (desktop only) — the element that
   * `contentOverflow` applies to. Needed to drive scroll position from outside,
   * e.g. returning from a sub-page scrolled to the bottom. Note
   * `contentContainerClassName` styles the inner wrapper, not this scroller.
   */
  contentRef?: React.Ref<HTMLDivElement>;
  initialFocus?: UseFocusTrapOptions["initialFocus"];
} & BottomSheetDialogProps;

export const ResponsiveModal = ({
  isOpen,
  onClose,
  isMobile,
  subtitle,
  showCloseButton = true,
  children,
  footer,
  onBack,
  backLabel = "Back",
  maxWidth = 512,
  maxHeight,
  mobileSheetDraggable = false,
  mobileSheetTitleType,
  modalClassName,
  headerClassName,
  contentContainerClassName,
  footerContainerClassName,
  contentOverflow = "auto",
  isDismissable = true,
  disableEscapeHandling = false,
  contentRef,
  initialFocus = "none",
  ...dialogProps
}: ResponsiveModalProps) => {
  const { title, ariaLabel, trapFocus = false } = dialogProps;
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Mobile renders a BottomSheet, which locks scroll and traps focus itself.
  useBodyScrollLock(isOpen && !isMobile);
  useFocusTrap(panelRef, trapFocus && isOpen && !isMobile, { initialFocus });

  // Capture phase + stopPropagation so this only ever closes the topmost
  // layer: it runs before any bubble-phase listener beneath it (e.g. a
  // Drawer's own Escape handler) gets a chance to also fire.
  useEffect(() => {
    if (isMobile || !isOpen || !isDismissable || disableEscapeHandling) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [isMobile, isOpen, isDismissable, disableEscapeHandling, onClose]);

  if (isMobile) {
    return (
      <BottomSheet
        {...dialogProps}
        isOpen={isOpen}
        onClose={onClose}
        titleType={mobileSheetTitleType}
        subtitle={subtitle}
        footer={footer}
        onBack={onBack}
        backLabel={backLabel}
        isDraggable={mobileSheetDraggable}
        isDismissable={isDismissable}
        initialFocus={initialFocus}
        showCloseButton={showCloseButton ? undefined : false}
      >
        {children}
      </BottomSheet>
    );
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        data-testid="modal_backdrop"
        className="absolute inset-0 bg-black/50"
        onClick={isDismissable ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role={trapFocus ? "dialog" : undefined}
        aria-modal={trapFocus ? true : undefined}
        aria-labelledby={trapFocus && title ? titleId : undefined}
        aria-label={trapFocus && !title ? ariaLabel : undefined}
        tabIndex={trapFocus ? -1 : undefined}
        className={`relative flex max-h-[calc(100vh-32px)] w-full flex-col rounded-2xl bg-surface-primary py-8 shadow-xl outline-none ${modalClassName ?? ""}`}
        style={{
          maxWidth,
          maxHeight:
            maxHeight !== undefined
              ? `min(${maxHeight}px, calc(100vh - 32px))`
              : undefined,
        }}
      >
        <div
          className={`mb-4 flex shrink-0 flex-col px-8 ${headerClassName ?? ""}`}
        >
          {(onBack || showCloseButton) && (
            <div className="flex items-center justify-between">
              {onBack ? (
                <button
                  data-testid="responsive_modal_back_button"
                  type="button"
                  onClick={onBack}
                  className="flex items-center gap-2 text-text-secondary"
                  aria-label={backLabel}
                >
                  <ArrowLeftIcon height={24} width={24} />
                  <Typo type="labelSM" textColor="text-text-secondary">
                    {backLabel}
                  </Typo>
                </button>
              ) : (
                <div />
              )}
              {showCloseButton && (
                <button
                  data-testid="responsive_modal_close_button"
                  type="button"
                  onClick={onClose}
                  className="rounded-md p-1 transition-colors hover:bg-overlay-opacity-state-hover"
                  aria-label="Close"
                >
                  <CrossIcon className="h-6 w-6 text-icon-secondary" />
                </button>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3">
            <div id={titleId}>
              <Typo element="h2" type="headingXL">
                {title}
              </Typo>
            </div>
            {subtitle && (
              <Typo element="p" type="bodyMD" textColor="text-text-tertiary">
                {subtitle}
              </Typo>
            )}
          </div>
        </div>

        <div
          ref={contentRef}
          className={`flex min-h-0 flex-1 flex-col ${contentOverflow === "visible" ? "overflow-visible" : "overflow-y-auto"}`}
        >
          <div
            className={`flex flex-col gap-6 px-8 ${contentContainerClassName ?? ""}`}
          >
            {children}
          </div>
        </div>

        {footer && (
          <div
            className={`mt-6 shrink-0 px-8 ${footerContainerClassName ?? ""}`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
